/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { AxiosError } from 'axios';
import {
  OriginType,
  PitStrategyData,
  RaceProgressionData,
  RunType,
  SessionPitRoadAnalysis,
  SessionPitRoadSummary,
  SessionTimingScoringData,
} from 'models/viewModels';
import { MotorsportsError, MotorsportsErrorType } from 'services/motorsportsError/MotorsportsError';
import { historicalRaceServiceApi } from './HistoricalRaceApi';

interface HistoricalRaceService {
  getReplaySessionTimingScoringData(
    eventId: string,
    sessionType: RunType,
    sessionId: string
  ): Promise<SessionTimingScoringData[] | undefined>;

  getReplaySessionProgressionData(
    eventId: string,
    sessionType: RunType,
    sessionId: string
  ): Promise<RaceProgressionData[] | undefined>;

  getReplaySessionPitRoadAnalysisData(
    eventId: string,
    sessionType: RunType
  ): Promise<SessionPitRoadAnalysis[] | undefined>;

  getReplaySessionPitRoadSummaryData(
    eventId: string,
    sessionType: RunType,
    sessionId: string,
    originType: OriginType
  ): Promise<SessionPitRoadSummary[] | undefined>;
}

class HistoricalRaceServiceServer implements HistoricalRaceService {
  async getReplaySessionTimingScoringData(
    eventId: string,
    sessionType: RunType,
    sessionId: string
  ): Promise<SessionTimingScoringData[] | undefined> {
    return await historicalRaceServiceApi
      .getReplaySessionTimingScoringData(eventId, sessionType, sessionId)
      .then((data) => data?.sessionTimingScoringDataList)
      .catch((error: AxiosError) => {
        throw new MotorsportsError(error.message, MotorsportsErrorType.Unknown, { cause: error });
      });
  }

  async getReplaySessionProgressionData(
    eventId: string,
    sessionType: RunType,
    sessionId: string
  ): Promise<RaceProgressionData[] | undefined> {
    return await historicalRaceServiceApi
      .getReplaySessionProgressionData(eventId, sessionType, sessionId)
      .then((data) => data?.progressionData)
      .catch((error: AxiosError) => {
        throw new MotorsportsError(error.message, MotorsportsErrorType.Unknown, { cause: error });
      });
  }

  async getReplaySessionPitRoadAnalysisData(
    eventId: string,
    sessionType: RunType
  ): Promise<SessionPitRoadAnalysis[] | undefined> {
    return await historicalRaceServiceApi
      .getReplaySessionPitRoadAnalysisData(eventId, sessionType)
      .then((data) => data.pitRoadAnalysis)
      .catch((error: AxiosError) => {
        throw new MotorsportsError(error.message, MotorsportsErrorType.Unknown, { cause: error });
      });
  }

  async getReplaySessionPitRoadSummaryData(
    eventId: string,
    sessionType: RunType,
    sessionId: string,
    originType: OriginType
  ): Promise<SessionPitRoadSummary[] | undefined> {
    return await historicalRaceServiceApi
      .getReplaySessionPitRoadSummaryData(eventId, sessionType, sessionId, originType)
      .then((data) => data?.pitRoadSummaryList)
      .catch((error: AxiosError) => {
        throw new MotorsportsError(error.message, MotorsportsErrorType.Unknown, { cause: error });
      });
  }

  async getReplayPitStrategyData(
    eventId: string,
    sessionType: RunType,
    sessionId: string
  ): Promise<PitStrategyData[] | undefined> {
    return await historicalRaceServiceApi
      .getReplayPitStrategyData(eventId, sessionType, sessionId)
      .then((data) => data?.pitStrategyDataList)
      .catch((error: AxiosError) => {
        throw new MotorsportsError(error.message, MotorsportsErrorType.Unknown, { cause: error });
      });
  }
}

export const historicalRaceServiceServer = new HistoricalRaceServiceServer();
